if (!customElements.get('ul-product-configurator')) {
  customElements.define('ul-product-configurator', class UlProductConfigurator extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.formId = this.dataset.formId;
      this.url = this.dataset.url || window.location.pathname;
      this.variantsSrc = this.dataset.variantsSrc || '';
      this.fieldsTarget = this.querySelector('[data-fields]');
      this.statusTarget = this.querySelector('[data-status]');
      this.options = this.readJson('product-options') || [];
      this.variants = [];
      this.nativeOptions = [];
      this.fields = [];
      this.selection = {};
      this.selects = new Map();
      this.currentVariantId = null;

      this.initialize();
    }

    async initialize() {
      const inlinePayload = this.readJson('product-variants');
      this.variants = this.normalizeVariants(inlinePayload);

      if (this.shouldLoadExternalVariants()) {
        const externalVariants = await this.fetchExternalVariants();
        if (externalVariants.length > 0) {
          this.variants = externalVariants;
        }
      }

      this.nativeOptions = this.buildNativeOptions();
      this.fields = this.buildFields();
      await this.refreshFromProductJsonIfNeeded();

      if (!this.shouldActivate()) {
        return;
      }

      const initialVariant = this.getInitialVariant();
      this.setSelectionFromVariant(initialVariant);
      this.renderFields();
      this.normalizeSelections();
      this.removeAttribute('hidden');
      this.closest('.product-info')?.classList.add('ul-configurator-active');
      this.updateProductState({ render: false });
    }

    readJson(id) {
      const node = this.querySelector(`[type="application/json"][data-id="${id}"]`);
      if (!node) return null;

      try {
        return JSON.parse(node.textContent || 'null');
      } catch (error) {
        console.warn('Unable to parse product configurator JSON.', error);
        return null;
      }
    }

    shouldLoadExternalVariants() {
      return Boolean(this.variantsSrc) && this.variants.length === 0;
    }

    async fetchExternalVariants() {
      try {
        const response = await fetch(this.variantsSrc, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) return [];

        const payload = await response.json();
        if (Array.isArray(payload?.n) && payload.n.length > 0) {
          this.options = payload.n.map((name) => String(name || ''));
        }

        return this.normalizeVariants(payload);
      } catch (error) {
        console.warn('Unable to load product configurator variant matrix.', error);
        return [];
      }
    }

    async refreshFromProductJsonIfNeeded() {
      if (!this.hasVisiblePackedFields()) return;

      try {
        const fresh = await this.fetchFreshProductJson();
        if (!fresh.variants.length || fresh.variants.length < this.variants.length) return;

        this.options = fresh.options;
        this.variants = fresh.variants;
        this.nativeOptions = this.buildNativeOptions();
        this.fields = this.buildFields();
      } catch (error) {
        console.warn('Unable to refresh product configurator from product JSON.', error);
      }
    }

    async fetchFreshProductJson() {
      const productUrl = `${this.url}.js?ul_configurator_cb=${Date.now()}`;
      const response = await fetch(productUrl, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return { options: [], variants: [] };

      const product = await response.json();
      const options = (product?.options || [])
        .map((option) => String(option?.name || option || ''))
        .filter(Boolean);
      const variants = (product?.variants || []).map((variant) => ({
        ...variant,
        options: variant.options || [variant.option1, variant.option2, variant.option3].filter(Boolean),
      }));

      return { options, variants };
    }

    normalizeVariants(payload) {
      if (!payload) return [];
      if (Array.isArray(payload)) return payload;

      if (Array.isArray(payload?.v) && Array.isArray(payload?.o)) {
        const optionValues = payload.o;
        const optionCount = optionValues.length;

        return payload.v.map((row) => ({
          id: row[0],
          sku: row[1] || '',
          available: Boolean(row[2]),
          options: optionValues.map((values, index) => values[row[3 + index]] || ''),
          barcode: '',
          featured_media: null,
        })).filter((variant) => variant.options.length === optionCount);
      }

      return [];
    }

    hasVisiblePackedFields() {
      if (!this.fields.length) return false;

      return this.fields.some((field) => {
        return this.suspiciousText(field.label)
          || field.values.some((value) => this.suspiciousText(value));
      });
    }

    suspiciousText(value) {
      const text = String(value || '');
      return text.includes('|') || /^Options:/i.test(text);
    }

    shouldActivate() {
      return this.options.length > 0
        && this.variants.length > 0
        && this.fields.length > this.nativeOptions.length
        && this.fieldsTarget;
    }

    buildNativeOptions() {
      return this.options.map((name, index) => {
        const values = [];
        this.variants.forEach((variant) => {
          const value = variant?.options?.[index];
          if (value && !values.some((existing) => this.equals(existing, value))) {
            values.push(value);
          }
        });

        return { name, index, values };
      });
    }

    buildFields() {
      const fields = [];

      this.nativeOptions.forEach((option) => {
        const nameParts = this.splitParts(option.name);
        const valueParts = option.values.map((value) => this.splitParts(value));
        const canSplit = nameParts.length > 1
          && option.values.length > 0
          && valueParts.every((parts) => parts.length === nameParts.length);

        if (canSplit) {
          nameParts.forEach((name, partIndex) => {
            fields.push({
              id: `option-${option.index}-part-${partIndex}`,
              label: this.cleanLabel(name),
              nativeIndex: option.index,
              partIndex,
              split: true,
              values: this.unique(valueParts.map((parts) => parts[partIndex])),
            });
          });
          return;
        }

        fields.push({
          id: `option-${option.index}`,
          label: this.cleanLabel(option.name),
          nativeIndex: option.index,
          partIndex: null,
          split: false,
          values: option.values,
        });
      });

      return fields;
    }

    splitParts(value) {
      return String(value || '')
        .split('|')
        .map((part) => part.replace(/\s+/g, ' ').trim())
        .filter(Boolean);
    }

    cleanLabel(value) {
      return String(value || '')
        .replace(/^options:\s*/i, '')
        .replace(/\s+/g, ' ')
        .trim();
    }

    unique(values) {
      const result = [];
      values.forEach((value) => {
        if (value && !result.some((existing) => this.equals(existing, value))) {
          result.push(value);
        }
      });
      return result;
    }

    equals(left, right) {
      return String(left || '').trim().toLowerCase() === String(right || '').trim().toLowerCase();
    }

    getInitialVariant() {
      const params = new URLSearchParams(window.location.search);
      const variantId = params.get('variant');
      const selected = variantId
        ? this.variants.find((variant) => String(variant.id) === String(variantId))
        : null;

      return selected
        || this.variants.find((variant) => variant.available)
        || this.variants[0]
        || null;
    }

    setSelectionFromVariant(variant) {
      if (!variant) return;
      this.selection = this.selectionFromVariant(variant);
    }

    selectionFromVariant(variant) {
      const selection = {};
      if (!variant) return selection;
      this.fields.forEach((field) => {
        const nativeValue = variant.options?.[field.nativeIndex] || '';

        if (field.split) {
          const parts = this.splitParts(nativeValue);
          selection[field.id] = parts[field.partIndex] || field.values[0] || '';
        } else {
          selection[field.id] = nativeValue || field.values[0] || '';
        }
      });

      return selection;
    }

    renderFields() {
      this.fieldsTarget.replaceChildren();

      this.fields.forEach((field) => {
        const fieldId = `${this.sectionId}-${field.id}`;
        const wrapper = document.createElement('div');
        wrapper.className = 'product-form-input product-form-input-dropdown ul-product-configurator-field';

        const label = document.createElement('label');
        label.setAttribute('for', fieldId);
        label.textContent = field.label;

        const select = document.createElement('select');
        select.id = fieldId;
        select.dataset.fieldId = field.id;

        field.values.forEach((value) => {
          const option = document.createElement('option');
          option.value = value;
          option.textContent = value;
          select.append(option);
        });

        select.value = this.selection[field.id] || field.values[0] || '';
        select.addEventListener('change', () => {
          this.selection[field.id] = select.value;
          this.normalizeSelections(field.id);
          this.updateProductState({ render: true });
        });

        wrapper.append(label);
        wrapper.append(select);
        this.fieldsTarget.append(wrapper);
        this.selects.set(field.id, select);
      });
    }

    normalizeSelections(lockedFieldId = null) {
      const maxPasses = Math.max(3, this.fields.length * 2);

      for (let pass = 0; pass < maxPasses; pass += 1) {
        this.fields.forEach((field) => {
          if (field.id === lockedFieldId && this.selection[field.id]) return;

          const possibleValues = this.getPossibleValues(field);
          const current = this.selection[field.id];

          if (!current) {
            this.selection[field.id] = possibleValues[0] || field.values[0] || '';
          } else if (possibleValues.length > 0 && !possibleValues.some((value) => this.equals(value, current))) {
            this.selection[field.id] = possibleValues[0];
          }
        });
      }

      if (lockedFieldId && !this.findVariant(this.selection)) {
        const nearestVariant = this.findNearestVariantForLockedField(lockedFieldId);
        if (nearestVariant) {
          this.setSelectionFromVariant(nearestVariant);
          this.syncSelects();
          return;
        }

        this.normalizeSelections();
        return;
      }

      this.syncSelects();
    }

    findNearestVariantForLockedField(lockedFieldId) {
      const lockedField = this.fields.find((field) => field.id === lockedFieldId);
      const lockedValue = lockedField ? this.selection[lockedField.id] : '';
      if (!lockedField || !lockedValue) return null;

      let best = null;
      this.variants.forEach((variant, index) => {
        const selection = this.selectionFromVariant(variant);
        if (!this.equals(selection[lockedField.id], lockedValue)) return;

        const score = this.fields.reduce((total, field) => {
          if (field.id === lockedField.id) return total + 4;
          return this.equals(selection[field.id], this.selection[field.id]) ? total + 1 : total;
        }, variant.available ? 2 : 0);

        if (!best || score > best.score || (score === best.score && index < best.index)) {
          best = { variant, score, index };
        }
      });

      return best?.variant || null;
    }

    syncSelects() {
      this.fields.forEach((field) => {
        const select = this.selects.get(field.id);
        if (!select) return;

        Array.from(select.options).forEach((option) => {
          option.disabled = false;
        });

        select.value = this.selection[field.id] || '';
      });
    }

    getPossibleValues(field) {
      return field.values.filter((value) => {
        const testSelection = { ...this.selection, [field.id]: value };
        return Boolean(this.findVariant(testSelection));
      });
    }

    getNativeValues(selection) {
      return this.nativeOptions.map((option) => {
        const splitFields = this.fields.filter((field) => field.nativeIndex === option.index && field.split);

        if (splitFields.length > 0) {
          return option.values.find((value) => {
            const parts = this.splitParts(value);
            return splitFields.every((field) => this.equals(parts[field.partIndex], selection[field.id]));
          }) || null;
        }

        const field = this.fields.find((item) => item.nativeIndex === option.index);
        return field ? selection[field.id] : null;
      });
    }

    findVariant(selection) {
      const nativeValues = this.getNativeValues(selection);
      if (nativeValues.some((value) => !value)) return null;

      return this.variants.find((variant) => {
        return nativeValues.every((value, index) => this.equals(variant.options?.[index], value));
      }) || null;
    }

    updateProductState({ render }) {
      const variant = this.findVariant(this.selection);

      if (!variant) {
        this.setStatus('This configuration is not available.');
        this.toggleAddButton(true, window.variantStrings?.unavailable || 'Unavailable');
        return;
      }

      const hasVariantParam = new URLSearchParams(window.location.search).has('variant');

      this.updateVariantInput(variant);
      if (render || hasVariantParam) {
        this.updateMedia(variant);
      }
      if (render) {
        this.updateUrl(variant);
      }
      this.setStatus(variant.sku ? `SKU: ${variant.sku}` : '');
      this.toggleAddButton(!variant.available, window.variantStrings?.outOfStock || 'Out of stock');
      this.setSku(variant.sku);
      this.setBarcode(variant.barcode);
      this.publishVariantChange(variant);

      if (render && String(this.currentVariantId) !== String(variant.id)) {
        this.renderProductInfo(variant);
      }

      this.currentVariantId = variant.id;
    }

    updateVariantInput(variant) {
      const forms = document.querySelectorAll(`#product-form-${this.sectionId}, #product-form-installment`);
      forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        if (!input) return;

        input.disabled = false;
        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    updateMedia(variant) {
      const mediaGallery = document.getElementById(`MediaGallery-${this.sectionId}`);
      const mediaId = variant?.featured_media?.id;

      if (mediaId && mediaGallery?.setActiveMedia) {
        mediaGallery.setActiveMedia(mediaId, mediaGallery.getAttribute('hide-variants') === 'true');
      }
    }

    updateUrl(variant) {
      if (!variant?.id || !window.history?.replaceState) return;
      window.history.replaceState({}, '', `${this.url}?variant=${variant.id}`);
    }

    renderProductInfo(variant) {
      fetch(`${this.url}?variant=${variant.id}&section_id=${this.sectionId}`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          this.replaceHtml(`price-${this.sectionId}`, html);
          this.replaceHtml(`ProductAvailability-${this.sectionId}`, html);
          this.replaceAddButton(html);
          this.toggleAddButton(!variant.available, window.variantStrings?.outOfStock || 'Out of stock');
          this.setSku(variant.sku);
          this.setBarcode(variant.barcode);
          this.publishVariantChange(variant, html);
        })
        .catch(() => {
          this.toggleAddButton(!variant.available, window.variantStrings?.outOfStock || 'Out of stock');
          this.setSku(variant.sku);
          this.setBarcode(variant.barcode);
          this.publishVariantChange(variant);
        });
    }

    replaceHtml(id, sourceDocument) {
      const destination = document.getElementById(id);
      const source = sourceDocument.getElementById(id);
      if (destination && source) {
        destination.innerHTML = source.innerHTML;
        destination.classList.remove('visibility-hidden');
      }
    }

    replaceAddButton(sourceDocument) {
      const productForm = document.getElementById(`product-form-${this.sectionId}`);
      const destination = productForm?.querySelector('[name="add"]');
      const source = sourceDocument.getElementById(`product-form-${this.sectionId}`)?.querySelector('[name="add"]');

      if (destination && source) {
        destination.innerHTML = source.innerHTML;
      }
    }

    toggleAddButton(disable, text) {
      const productForm = document.getElementById(`product-form-${this.sectionId}`);
      const addButton = productForm?.querySelector('[name="add"]');
      const addButtonText = addButton?.querySelector('span');

      if (!addButton) return;

      if (disable) {
        addButton.setAttribute('disabled', 'disabled');
      } else {
        addButton.removeAttribute('disabled');
      }

      if (disable && text && addButtonText) {
        addButtonText.textContent = text;
      }
    }

    setSku(sku) {
      const skuField = document.getElementById(`ProductSku-${this.sectionId}`);
      window.updateProductFastShip?.(this.sectionId, sku);

      if (!skuField) return;

      if (!sku) {
        skuField.parentNode.classList.add('visually-hidden');
        return;
      }

      skuField.parentNode.classList.remove('visually-hidden');
      skuField.textContent = sku;
    }

    setBarcode(barcode) {
      const barcodeField = document.getElementById(`ProductBarcode-${this.sectionId}`);
      if (!barcodeField) return;

      if (!barcode) {
        barcodeField.parentNode.classList.add('visually-hidden');
        return;
      }

      barcodeField.parentNode.classList.remove('visually-hidden');
      barcodeField.textContent = barcode;
    }

    setStatus(message) {
      if (this.statusTarget) {
        this.statusTarget.textContent = message || '';
      }
    }

    publishVariantChange(variant, html = null) {
      const detail = { sectionId: this.sectionId, variant };
      document.dispatchEvent(new CustomEvent('product:variant-change', { detail: { variant } }));

      try {
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.productVariantChange, html ? { ...detail, html } : detail);
        }
      } catch (error) {
        // Keep variant selection working even if the theme event bus is unavailable.
      }
    }
  });
}
